import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import type { Place } from "../page-location.ts";
import type { RiderApi } from "../rider-api.ts";
import { SCOPES } from "../session.ts";
import { Absent, PhoneShell } from "./PhoneShell.tsx";
import { MapHero } from "./Hero.tsx";

/**
 * `1 Where to` — the first screen of the flow, and the only one of the nine that needs no identifier.
 *
 * Four rows under the greeting, and the design derives all four from one array:
 *
 *   1. **Where the rider is now**, as an address. GEO-02's `POST /v1/reverse-geocode` turns the
 *      device's own fix into `address.formatted`, and it is the only operation on this platform that
 *      can. The fix itself is the browser's, which no service can answer.
 *   2. **The destination search.** GEO-01's `POST /v1/geocode` answers `results.formattedAddress`
 *      and a position for each.
 *   3. **and 4. The rider's saved places** — `Home · Kilimani` and `Work · Upper Hill`. CUS-02
 *      publishes `POST /v1/customers/{customerId}/saved-places` and
 *      `GET …/saved-places/{placeId}`, and **no listing**. So a rider holding no `placeId` cannot
 *      discover their own saved places, and this is the one measured absence on this screen.
 *
 * ## Two rows become one, and that is deliberate rather than a liberty
 *
 * The design draws two saved-place rows. Drawing two dashed pills would assert that there are two
 * saved places, which is a count this platform publishes nowhere — R-04 measured that **no listing
 * on this platform publishes a total**, and a count invented in a layout is the same fault as a
 * count invented in a figure. So the slot keeps its place in the order and carries one absence
 * saying the list cannot be read. It is a visible difference from the design, it is reported as a
 * measured number rather than closed, and the alternative was worse.
 */

export interface WhereToProps {
  readonly api: RiderApi;
  readonly scopes: ReadonlySet<string>;
  readonly givenName: string;
  /** Chosen, which moves to `2 Choose` with the route in the address. */
  readonly onDestination: (input: {
    readonly origin: Place;
    readonly destination: Place;
    readonly destinationLabel: string;
  }) => void;
  /**
   * The device's position, injected rather than read here.
   *
   * `navigator.geolocation` is the browser's and is not a service, so it is a parameter: the tests
   * and the acceptance check supply a known fix, and a real phone supplies its own. A component that
   * reached for the global directly would be a component that cannot be put into a known state,
   * which is exactly what `CLAUDE.md`'s step 1 requires of every screen.
   */
  readonly position: DevicePosition;
}

/** What the browser says about where the phone is. Three answers, and two of them are not a place. */
export type DevicePosition =
  | { readonly kind: "locating" }
  | { readonly kind: "at"; readonly place: Place; readonly accuracyMetres: number }
  /** Permission refused, no sensor, or the fix timed out. Each is "we cannot say where you are". */
  | { readonly kind: "unavailable"; readonly because: string };

export function WhereTo(props: WhereToProps): ReactNode {
  const { api, position } = props;
  const [originLabel, setOriginLabel] = useState<string | null>(null);
  const [originBecause, setOriginBecause] = useState("Finding where you are");
  const [text, setText] = useState("");
  const [results, setResults] = useState<readonly { id: string; label: string; at: Place }[]>([]);

  /*
   * The address of where the rider is, from the fix the browser gave.
   *
   * Re-asked whenever the fix changes, which on a phone walking to a kerb is often — and GEO-02's
   * own `meta.cacheHit` exists because the service expects that. The accuracy is forwarded rather
   * than defaulted: a reverse geocode of a fix with a two-kilometre radius is a different question
   * from one with a five-metre radius, and GEO-02 is told which it is being asked.
   */
  useEffect(() => {
    if (position.kind === "locating") {
      setOriginLabel(null);
      setOriginBecause("Finding where you are");
      return;
    }
    if (position.kind === "unavailable") {
      setOriginLabel(null);
      setOriginBecause(position.because);
      return;
    }
    let current = true;
    api
      .reverseGeocode({
        latitude: position.place.latitude,
        longitude: position.place.longitude,
        accuracyMeters: position.accuracyMetres,
      })
      .then((answer) => {
        if (current) setOriginLabel(answer.address.formatted);
      })
      .catch(() => {
        if (current) {
          setOriginLabel(null);
          setOriginBecause("No address for this spot");
        }
      });
    return (): void => {
      current = false;
    };
  }, [api, position]);

  /*
   * The destination search, asked after the typing stops rather than on every keystroke.
   *
   * 250ms, which is long enough that a word typed at speed is one request and short enough that the
   * list does not feel late. GEO-01 is a paid upstream for most tenants and a request per keystroke
   * would be a tenant's geocoding bill as a function of typing speed.
   */
  const search = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    if (search.current !== undefined) clearTimeout(search.current);
    const query = text.trim();
    if (query.length < 3) {
      setResults([]);
      return;
    }
    search.current = setTimeout(() => {
      api
        .geocode({
          text: query,
          limit: 5,
          ...(position.kind === "at" ? { proximity: position.place } : {}),
        })
        .then((answer) => {
          setResults(
            answer.results.map((result) => ({
              id: result.id,
              label: result.formattedAddress,
              at: result.position,
            })),
          );
        })
        .catch(() => setResults([]));
    }, 250);
    return (): void => {
      if (search.current !== undefined) clearTimeout(search.current);
    };
  }, [api, text, position]);

  const choose = useCallback(
    (destination: Place, label: string) => {
      if (position.kind !== "at") return;
      props.onDestination({
        origin: position.place,
        destination,
        destinationLabel: label,
      });
    },
    [position, props],
  );

  const canSearch = props.scopes.has(SCOPES.geocode);

  return (
    <PhoneShell
      screen="where"
      /*
       * The rider's own position, where the device gave one.
       *
       * This is the one place this screen knows for certain, and with a maps key it is the map's
       * centre. The other two answers — `locating`, and `unavailable` with a reason — carry no
       * coordinate, so `places` is absent and `MapHero` draws the design's schematic, which is
       * what this screen drew before and still draws on a phone with location switched off.
       */
      hero={
        <MapHero
          kind="home"
          {...(position.kind === "at" ? { places: { origin: position.place } } : {})}
        />
      }
      /*
       * `menu` rather than `arrow_back`, which is the design's own first-screen control.
       *
       * It opens nothing. The design draws no drawer anywhere in the file — no menu contents, no
       * account screen, no list of destinations behind it — so there is nothing to open, and
       * inventing a drawer would be adding a section that is not in the design. The control is kept
       * because removing it is equally forbidden, and it says so when pressed.
       */
      back={{
        glyph: "menu",
        label: "Menu",
      }}
      safety={{ armed: false }}
      chip={{ class: "idle", label: `Hi ${props.givenName}` }}
      title="Where to?"
      sub="Book now or schedule for later."
      actions={[
        {
          label: "Schedule a ride",
          glyph: "event",
          primary: false,
          /*
           * Drawn, and it cannot work.
           *
           * MOB-03's `POST /v1/ride-requests` takes `quoteId` and nothing else — no `scheduledFor`,
           * no `pickupAt`, no window — and nothing else on this platform accepts a future ride. So
           * the button is drawn because the design draws it and nothing may be removed, and it says
           * why rather than appearing to work.
           */
          unavailable:
            "Nothing published accepts a ride for later: POST /v1/ride-requests takes a quoteId and no time.",
        },
      ]}
    >
      {/* Row 1: where the rider is now. */}
      <div className="field field--origin">
        <span className="glyph field__glyph" aria-hidden="true">
          trip_origin
        </span>
        {originLabel === null ? (
          <Absent words={originBecause} />
        ) : (
          <span className="field__text">{originLabel}</span>
        )}
      </div>

      {/* Row 2: the destination search, which the design draws as the focused field. */}
      <div className="field field--search">
        <span className="glyph field__glyph" aria-hidden="true">
          search
        </span>
        <input
          className="field__input"
          type="search"
          value={text}
          placeholder="Search destination"
          aria-label="Search destination"
          disabled={!canSearch}
          onChange={(event) => setText(event.target.value)}
        />
      </div>

      {/*
       * Rows 3 and 4: the saved places, as one absence.
       *
       * CUS-02 publishes the `POST` that creates one and the `GET` that reads one **by its own
       * identifier**, and no listing — so a rider opening this application holds no `placeId` and
       * has no way to get one. The glyph is the design's own first saved-place glyph, so the row is
       * recognisably the row the design draws.
       */}
      <div className="field field--saved">
        <span className="glyph field__glyph" aria-hidden="true">
          home
        </span>
        <Absent words="Saved places can't be listed" />
      </div>

      {results.length === 0 ? null : (
        <div className="results" role="listbox" aria-label="Destinations found">
          {results.map((result) => (
            <button
              key={result.id}
              type="button"
              className="results__item"
              onClick={() => choose(result.at, result.label)}
            >
              <span className="glyph" aria-hidden="true">
                location_on
              </span>
              <span className="results__label">{result.label}</span>
            </button>
          ))}
        </div>
      )}
    </PhoneShell>
  );
}
